import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { milestonesApi } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../lib/utils';
/**
 * Compact milestone widget for the Dashboard.
 * Renders ONLY when the active account is personal (organisationId === null).
 * Callers are responsible for the personal-account check, but this component
 * also guards itself so it's safe to render unconditionally.
 */
export function MilestoneDashboardWidget() {
    const { activeAccount } = useAuth();
    const navigate = useNavigate();
    const isPersonal = !activeAccount?.organisationId;
    const { data } = useQuery({
        queryKey: ['milestones-summary', activeAccount?.id],
        queryFn: () => milestonesApi.summary(),
        enabled: Boolean(activeAccount) && isPersonal,
        // Don't show stale banners — only show confirmed awards
        staleTime: 60_000,
    });
    // Guard: nothing for org accounts or while loading
    if (!isPersonal || !data?.isPersonal)
        return null;
    if (data.total === 0)
        return null;
    const recent = data.recent;
    return (_jsxs("div", { className: "card cursor-pointer hover:shadow-sm transition-shadow", onClick: () => navigate('/milestones'), role: "button", tabIndex: 0, onKeyDown: (e) => e.key === 'Enter' && navigate('/milestones'), "aria-label": "View milestones", children: [_jsxs("div", { className: "px-5 py-4 border-b border-gray-100 flex items-center justify-between", children: [_jsxs("h2", { className: "section-heading flex items-center gap-2", children: [_jsx(Trophy, { className: "h-4 w-4 text-amber-500" }), "Milestones"] }), _jsxs("span", { className: "text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full", children: [data.total, " reached"] })] }), _jsxs("div", { className: "px-5 py-4", children: [recent ? (_jsxs("div", { children: [_jsx("p", { className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1", children: "Most recent" }), _jsx("p", { className: "text-sm font-semibold text-gray-900", children: recent.title }), _jsx("p", { className: "text-sm text-gray-500 mt-0.5", children: recent.description }), _jsx("p", { className: "text-xs text-gray-400 mt-1.5", children: formatDate(recent.awardedAt) })] })) : (_jsx("p", { className: "text-sm text-gray-500", children: "View your collection milestones." })), _jsx("p", { className: "text-xs text-wine-700 font-medium mt-3 hover:text-wine-800", children: "View all milestones \u2192" })] })] }));
}
